import React from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import _, { debounce } from "lodash";
import {
    useCreateDashboardStore,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from "..";
import CreateChartModal from "../modal/CreateChart";
import { ChartSource, ChartType } from "constant/chart";
import { MultipleDeviceChartBlock } from "../MultipleDeviceChartBlock";
import { BlockKeyProps } from "types/Charts";
import { getRandomString } from "utils/functions";
import { getMQTTQueryByDevice } from "../../utils/widgetUtil";
import { MultipleSensorChartType } from "constant";

const getId = () => getRandomString(7);

const MultipleActuatorCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: true,
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Widget Name",
        },
        {
            key: "sensorType",
            name: "Actuator Type",
        },
        {
            key: "actuators_count",
            name: "No. of Actuators",
            render: (data: any) => data.attributes?.length,
        },
        {
            key: "unit",
            name: "Unit",
            render: (data: any) => {
                const arrayOfUnits = data.unit
                    .split(",")
                    .reduce((acc: any, symbol: any) => {
                        if (symbol.trim()) acc.push(symbol);
                        return acc;
                    }, []);

                return arrayOfUnits.join(", ");
            },
        },
    ],
    transform: (data: any) => ({
        attributes: data.attributes.map((d: any) => ({
            color: d.color,
            device_id: d.ldsuUID,
            gateway_id: d.gatewayId,
            name: d.sensorType,
            said: String(d.said),
            time_series: getMQTTQueryByDevice(d),
            unit: d.unit === "BINARY" ? "" : d.unit,
        })),
        source: ChartSource.MULTIPLE_ACTUATORS,
        chart: ChartType.MULTIPLE_LINES,
        name: data.chart_name || "Multiple Actuator Chart",
    }),
    validate() {
        return this.data.map((item) => ({
            path: "",
            isValid: CHART_NAME_PATTERN.test(item.chart_name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

const DashboardCreateMultipleActuatorComponent: React.FunctionComponent =
    () => {
        const [multipleChartList, setMultipleChartList] = React.useState<
            BlockKeyProps[]
        >([]);
        const [multipleChartData, setMultipleChartData] =
            React.useState<object>({});
        const { createModalShow, setCreateModalShow } =
            useCreateDashboardStore();

        React.useEffect(() => {
            const createData: any[] = [];

            for (let blockData of Object.values(multipleChartData)) {
                const chart_name = blockData.chart_name;
                const chartDevices: any[] = [];

                const multipleChartType = blockData.multipleChartType as keyof typeof MultipleSensorChartType;

                if (MultipleSensorChartType[multipleChartType] === MultipleSensorChartType.same) {
                    chartDevices.push(...Object.values(blockData.data["0"].devices));
                } else {
                    Object.values(blockData.data).forEach((column: any) =>
                        chartDevices.push(...Object.values(column.devices))
                    );
                }

                const types = Object.keys(
                    chartDevices.pluck("sensorType")
                ).join(" x ");
                const units = Object.keys(chartDevices.pluck("unit")).join(
                    ", "
                );

                !!chartDevices.length &&
                    createData.push({
                        chart_name,
                        sensorType: types,
                        unit: units,
                        attributes: chartDevices,
                    });
            }

            useCreateDashboardStore.setState({
                createData: {
                    ...MultipleActuatorCreateData,
                    data: createData,
                },
            });
        }, [multipleChartData]);

        const handleOnDelete = (deleteItem: BlockKeyProps) => {
            setMultipleChartData((prev) => _.omit(prev, deleteItem.key));

            setMultipleChartList((prev) =>
                prev.filter((item) => item.key !== deleteItem.key)
            );
        };

        const createDebouncedHandler = (itemKey: string) => {
            return debounce((data: any) => {
                setMultipleChartData((prev: any) => ({
                    ...prev,
                    [itemKey]: data,
                }));
            }, 100);
        };

        return (
            <React.Fragment>
                <Row>
                    <Col sm="12">
                        <p className="reusable-description">
                            In a multi-actuator comparison chart, you can add
                            max 8 actuators (Actuators with same actuator type
                            or two different actuator types. i.e. 2 y-axis can
                            plot with different units)
                        </p>

                        {multipleChartList.map((item) => (
                            <React.Fragment key={item.key}>
                                <MultipleDeviceChartBlock
                                    deviceType="Actuator"
                                    blockKey={item}
                                    onDelete={handleOnDelete}
                                    onChange={createDebouncedHandler(item.key)}
                                />
                            </React.Fragment>
                        ))}

                        <Button
                            className="mt-3"
                            variant="primary"
                            onClick={() =>
                                setMultipleChartList((prev) => [
                                    ...prev,
                                    {
                                        key: getId(),
                                    },
                                ])
                            }
                        >
                            + Create Multiple Actuator Chart
                        </Button>

                        {multipleChartList.length > 0 && (
                            <p className="reusable-description mt-2">
                                Continue creating another multi-actuator
                                comparison chart. They’ll all be saved together
                                in a single step.
                            </p>
                        )}
                    </Col>
                </Row>

                <CreateChartModal
                    show={createModalShow && multipleChartList.length > 0}
                    onHide={() => setCreateModalShow(false)}
                />
            </React.Fragment>
        );
    };

export default React.memo(DashboardCreateMultipleActuatorComponent);
